import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Modal } from "@/config/antd";
import Button from "@/components/common/Button";
import { CloseOutlined } from "@/config/antd";
import Input from "@/components/common/inputs/Input";
import TimeRangeInput from "@/components/specific/Time_Range_Input";
import { useDispatch } from "react-redux";
import {
  addScheduleToSubEvent,
  updateScheduleInSubEvent,
} from "@/store/redux/slices/event/sub_event_slice";
import { useParams } from "react-router-dom";
import MemberPicker from "@/components/specific/Member_Picker";
import InviteMembersDialog from "@/ui/Invite_Members";
import Delete from "@/components/common/alerts/Delete";
import Success from "@/components/common/alerts/Success";

const ScheduleDialog = ({
  isVisible,
  onClose,
  schedule,
  dialogMode = "add",
  onSuccess,
  onError,
}) => {
  const [scheduleTitle, setScheduleTitle] = useState("");
  const [speakers, setSpeakers] = useState([]);
  const [fromTime, setFromTime] = useState("");
  const [toTime, setToTime] = useState("");
  const dispatch = useDispatch();
  const { id } = useParams();

  // Add states for managing modals and member operations
  const [isInviteMembersModalVisible, setIsInviteMembersModalVisible] =
    useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [isSuccessModalVisible, setIsSuccessModalVisible] = useState(false);

  useEffect(() => {
    if (isVisible && schedule) {
      // Set schedule title
      setScheduleTitle(schedule.name || "");

      // Set speakers - convert to the format expected by MemberPicker if needed
      if (schedule.speakers && Array.isArray(schedule.speakers)) {
        const formattedSpeakers = schedule.speakers.map((speaker) => {
          // (id, name, email, avatar, role)
          return {
            id: speaker._id || speaker.id,
            name: speaker.name || speaker.fullname,
            email: speaker.email || "",
            avatar: speaker.avatar || speaker.profileImage,
            role: speaker.role || "speaker",
          };
        });
        setSpeakers(formattedSpeakers.filter((speaker) => speaker.id)); // Filter out any invalid speakers
      } else {
        setSpeakers([]);
      }

      // Get duration from the schedule object
      if (schedule.duration) {
        // Convert from "11:45 AM" format to "11:45" (24-hour) format
        if (schedule.duration.startTime) {
          const startTime = schedule.duration.startTime;
          // Check if the time includes AM/PM format
          if (startTime.includes("AM") || startTime.includes("PM")) {
            const [time, period] = startTime.split(" ");
            const [hours, minutes] = time.split(":");
            let hour24 = parseInt(hours);

            if (period === "PM" && hour24 !== 12) {
              hour24 += 12;
            } else if (period === "AM" && hour24 === 12) {
              hour24 = 0;
            }

            setFromTime(`${hour24.toString().padStart(2, "0")}:${minutes}`);
          } else {
            setFromTime(startTime);
          }
        }

        if (schedule.duration.endTime) {
          const endTime = schedule.duration.endTime;
          // Check if the time includes AM/PM format
          if (endTime.includes("AM") || endTime.includes("PM")) {
            const [time, period] = endTime.split(" ");
            const [hours, minutes] = time.split(":");
            let hour24 = parseInt(hours);

            if (period === "PM" && hour24 !== 12) {
              hour24 += 12;
            } else if (period === "AM" && hour24 === 12) {
              hour24 = 0;
            }

            setToTime(`${hour24.toString().padStart(2, "0")}:${minutes}`);
          } else {
            setToTime(endTime);
          }
        }
      } else {
        setFromTime("");
        setToTime("");
      }
    }
  }, [isVisible, schedule]);

  const handleMembersSelected = (newMembers) => {
    setSpeakers((prev) => [
      ...prev,
      ...newMembers.filter(
        (member) => !prev.some((existing) => existing.id === member.id)
      ),
    ]);
  };

  const handleDeleteMember = (member) => {
    setMemberToDelete(member.id);
    setIsDeleteModalVisible(true);
  };

  const handleDeleteConfirm = () => {
    setSpeakers((prev) => prev.filter((m) => m.id !== memberToDelete));
    setIsDeleteModalVisible(false);
    setIsSuccessModalVisible(true);
  };

  const handleUpdateMember = (memberId, newRole) => {
    setSpeakers((prev) =>
      prev.map((member) =>
        member.id === memberId ? { ...member, role: newRole } : member
      )
    );
  };

  const handleFromTimeChange = (time) => {
    if (!time) {
      setFromTime("");
      return;
    }
    const date = new Date(`2000-01-01T${time}`);
    const formattedTime = date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
    setFromTime(formattedTime);
  };

  const handleToTimeChange = (time) => {
    if (!time) {
      setToTime("");
      return;
    }
    const date = new Date(`2000-01-01T${time}`);
    const formattedTime = date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
    setToTime(formattedTime);
  };

  const isFormValid = () => {
    return (
      scheduleTitle.trim() !== "" &&
      fromTime.trim() !== "" &&
      toTime.trim() !== ""
    );
  };

  const handleSave = async () => {
    if (!isFormValid()) {
      return;
    }

    // Prepare schedule data for submission
    const scheduleData = {
      name: scheduleTitle,
      speakers: speakers.map((speaker) => speaker.id), // Extract ONLY the IDs
      duration: {
        startTime: fromTime,
        endTime: toTime,
      },
    };

    try {
      let res;

      // When in modify mode, extract subEventId and scheduleId from the schedule object
      // that was passed from the ScheduleCard component
      const subEventId = schedule?.subEventId || schedule?._id || id;
      const scheduleId = schedule?.scheduleId;

      if (dialogMode === "add") {
        // Use the addScheduleToSubEvent endpoint
        res = await dispatch(
          addScheduleToSubEvent({
            subEventId: subEventId,
            scheduleData: scheduleData,
          })
        ).unwrap();
      } else {
        // For modify, use the new updateScheduleInSubEvent endpoint
        if (!scheduleId) {
          throw new Error("Cannot update schedule: Missing schedule ID");
        }

        const updateScheduleResponse = await dispatch(
          updateScheduleInSubEvent({
            subEventId: subEventId,
            scheduleId: scheduleId,
            scheduleData: scheduleData,
          })
        ).unwrap();

        res = updateScheduleResponse;
      }

      // Call onSuccess callback if provided
      if (onSuccess && typeof onSuccess === "function") {
        // Don't try to access res.data - just pass the full response or scheduleData if res is undefined
        onSuccess(dialogMode, res || scheduleData);
      }

      // Close the dialog
      onClose();
    } catch (error) {
      console.error(
        `Failed to ${
          dialogMode === "add" ? "add" : "update"
        } schedule - detailed error:`,
        error
      );

      // Call onError callback if provided
      if (onError && typeof onError === "function") {
        onError(
          dialogMode,
          error?.message ||
            `Failed to ${dialogMode === "add" ? "add" : "update"} schedule`
        );
      }
    }
  };

  const getDialogTitle = () => {
    return dialogMode === "add" ? "Add New Schedule" : "Modify Schedule";
  };

  const getActionButtonText = () => {
    return dialogMode === "add" ? "ADD SCHEDULE" : "UPDATE";
  };

  // Define role options for the speakers
  const roleOptions = [
    { value: "speaker", label: "Speaker" },
    { value: "moderator", label: "Moderator" },
    { value: "panelist", label: "Panelist" },
  ];

  return (
    <Modal
      open={isVisible}
      onCancel={onClose}
      footer={null}
      centered
      width={400}
      closeIcon={null}
      styles={{
        body: { padding: 0 },
        content: {
          padding: 0,
          borderRadius: "16px",
          overflow: "hidden",
        },
        mask: { backgroundColor: "rgba(0, 0, 0, 0.75)" },
      }}>
      <div className="flex flex-col bg-white">
        {/* Header with title and close button */}
        <div className="p-4 px-6 flex justify-between items-center">
          <h2 className="text-bold-x text-primary">{getDialogTitle()}</h2>
          <CloseOutlined
            onClick={onClose}
            className="cursor-pointer"
            style={{
              color: "var(--veegil-color-primary)",
              backgroundColor: "var(--veegil-color-secondary)",
              borderRadius: "50%",
              padding: "4px",
            }}
          />
        </div>

        {/* Content Area */}
        <div className="flex flex-col px-6 pb-6 space-y-6">
          {/* Schedule Info Section */}
          <div className="space-y-2">
            <h3 className="text-darkGrey text-bold font-semibold">
              SCHEDULE INFO
            </h3>
            <Input
              label="TITLE"
              placeholder="Enter Title"
              value={scheduleTitle}
              onChange={(e) => setScheduleTitle(e.target.value)}
            />
          </div>

          {/* Schedule Speaker Section */}
          <div className="space-y-2">
            <h3 className="text-darkGrey text-bold font-semibold">
              SCHEDULE SPEAKERS
            </h3>
            <MemberPicker
              members={speakers}
              roles={roleOptions}
              onOpenModal={() => setIsInviteMembersModalVisible(true)}
              onDeleteMember={handleDeleteMember}
              onUpdateMember={handleUpdateMember}
            />
          </div>

          {/* Duration Slot Section */}
          <div className="space-y-2">
            <h3 className="text-darkGrey text-bold font-semibold">
              DURATION SLOT
            </h3>
            <TimeRangeInput
              fromTime={fromTime}
              toTime={toTime}
              onFromChange={handleFromTimeChange}
              onToChange={handleToTimeChange}
            />
          </div>
        </div>

        {/* Footer Buttons */}
        <div className="w-full bg-secondary p-4 flex gap-3">
          <Button
            text="CANCEL"
            color="secondary"
            className="flex-1 border border-primary"
            customBgColor="white"
            customTextColor="primary"
            onClick={onClose}
          />
          <Button
            text={getActionButtonText()}
            color="primary"
            className={`flex-1 ${
              isFormValid()
                ? "bg-primary text-white"
                : "bg-gray-300 text-gray-600 cursor-not-allowed"
            }`}
            customBgColor={isFormValid() ? "primary" : "darkBlue"}
            customTextColor={isFormValid() ? "primary" : "primary"}
            onClick={handleSave}
            disabled={!isFormValid()}
          />
        </div>
      </div>

      {/* Invite Members Dialog */}
      <InviteMembersDialog
        isVisible={isInviteMembersModalVisible}
        onClose={() => setIsInviteMembersModalVisible(false)}
        onMembersSelected={handleMembersSelected}
        modalTitle="Add Speakers"
        primaryHeader="Invited Speakers"
        showSecondaryHeader={true}
        secondaryHeader="Other Members"
        userRoleDisplay="select"
        roleOptions={roleOptions}
        invitedMembers={speakers}
        emptyInvitedMessage="No speakers have been added yet."
        onUpdateMember={handleUpdateMember}
      />

      {/* Delete Confirmation Dialog */}
      <Delete
        isVisible={isDeleteModalVisible}
        onClose={() => setIsDeleteModalVisible(false)}
        onProceed={handleDeleteConfirm}
        header={`Remove ${speakers.find((m) => m.id === memberToDelete)?.name}`}
        paragraph={`Are you sure you want to remove <span class="text-primary">${
          speakers.find((m) => m.id === memberToDelete)?.name
        }</span> from this schedule?`}
        proceedButtonText="Remove"
        cancelButtonText="Cancel"
      />

      {/* Success Dialog */}
      <Success
        isVisible={isSuccessModalVisible}
        onClose={() => setIsSuccessModalVisible(false)}
        header="Speaker Removed"
        paragraph={`Speaker has been removed from this schedule.`}
        buttonText="Close"
        onButtonClick={() => setIsSuccessModalVisible(false)}
      />
    </Modal>
  );
};

ScheduleDialog.propTypes = {
  isVisible: PropTypes.bool,
  onClose: PropTypes.func,
  schedule: PropTypes.shape({
    _id: PropTypes.string,
    subEventId: PropTypes.string,
    scheduleId: PropTypes.string,
    name: PropTypes.string,
    eventTitle: PropTypes.string,
    time: PropTypes.string,
    speakers: PropTypes.array,
    duration: PropTypes.shape({
      startTime: PropTypes.string,
      endTime: PropTypes.string,
    }),
  }),
  dialogMode: PropTypes.oneOf(["add", "modify"]),
  onSuccess: PropTypes.func,
  onError: PropTypes.func,
};

export default ScheduleDialog;
